import { create } from 'zustand'
import { getCookie, removeCookie, setCookie } from '@/util/cookie.ts'
import { persist } from 'zustand/middleware'
import { QueryClient } from '@tanstack/react-query'

interface ILoginStore {
    accessToken: string
    login: (id: string, accessToken: string) => void
    logout: () => void
    isAutoLogin?: boolean
    isSaveID?: boolean
    savedID?: string
    setAutoLogin: (checked: boolean) => void
    setSaveID: (checked: boolean) => void
    saveID: (id: string) => void

}

export const KEY_ACCESS_TOKEN = 'access_token'
// Refresh Token은 서버의 HttpOnly Cookie로만 관리됨 (프론트엔드 접근 불가)
export const useLoginStore = create<ILoginStore>()(
    persist(
        (set, get) => ({
            isAutoLogin: false,
            isSaveID: false,
            accessToken: getCookie(KEY_ACCESS_TOKEN) || '',
            login: async (id = '', accessToken = '') => {
                set(_ => ({ accessToken: accessToken }))

                // Access Token을 쿠키에 저장 (2시간 유효)
                setCookie(KEY_ACCESS_TOKEN, accessToken, { 
                    path: '/', 
                    maxAge: 7200 // 2시간 (Access Token 1시간 + 버퍼 1시간)
                })

                if (get().isSaveID && id) {
                    set(_ => ({ savedID: id }))
                } else if (!get().isSaveID) {
                    set(_ => ({ savedID: '' }))
                }
            },
            logout: async () => {
                const currentAccessToken = getCookie(KEY_ACCESS_TOKEN)
                if (currentAccessToken) {
                    try {
                        const { apiLogout } = await import('@/api/auth/logout.ts')
                        await apiLogout()
                    } catch (error) {
                        // 로그아웃 실패해도 클라이언트는 정리
                    }
                }

                set(_ => ({ accessToken: '' }))
                removeCookie(KEY_ACCESS_TOKEN, { path: '/' })

                const queryClient = new QueryClient()
                queryClient.removeQueries()
            },
            setAutoLogin: (checked = false) => {
                set(_ => ({ isAutoLogin: checked }))
            },
            setSaveID: (checked = false) => {
                set(_ => ({ isSaveID: checked }))
                if (!checked) {
                    set(_ => ({ savedID: '' }))
                }
            },
            saveID: (id = '') => {
                set(_ => ({ savedID: id }))
            }
        }),
        {
            name: 'login-store',
            // accessToken은 쿠키로만 관리하고 localStorage에는 저장하지 않음
            partialize: (state) => ({
                isAutoLogin: state.isAutoLogin,
                isSaveID: state.isSaveID,
                savedID: state.savedID
                // accessToken은 제외 (쿠키로만 관리)
            })
        }
    )
)